import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Package, Clock, CheckCircle, XCircle, Truck, Eye, Calendar, ExternalLink, Star } from 'lucide-react';
import { ordersAPI } from '../services/api';
import { getOrderCarrierLabel, getOrderTrackingUrl } from '../utils/shippingCarriers';

// Image URL helper function - HTTPS uyumlu
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // Production'da HTTPS kullan
  if (process.env.NODE_ENV === 'production') {
    // HTTPS ile başlamıyorsa HTTPS ekle
    if (imagePath.startsWith('http://')) {
      return imagePath.replace('http://', 'https://');
    }
    // Relative path ise HTTPS domain ile birleştir
    if (imagePath.startsWith('/')) {
      return `https://leasertech.com${imagePath}`;
    }
    return `https://leasertech.com/${imagePath}`;
  }
  
  // Development'ta localhost kullan
  const API_BASE_URL = 'http://localhost:5000';
  return `${API_BASE_URL}${imagePath}`;
};

const Orders = () => {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await ordersAPI.getOrders({ limit: 50 });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
      const isTimeout = error.code === 'ECONNABORTED';
      setLoadError(
        isTimeout
          ? 'Siparişler yüklenirken zaman aşımı oluştu. Lütfen tekrar deneyin.'
          : 'Siparişler yüklenemedi. Lütfen tekrar deneyin.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadOrders();
    }
  }, [authLoading, isAuthenticated, loadOrders]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'beklemede': return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'onaylandi': return <Package className="h-5 w-5 text-blue-500" />;
      case 'hazirlaniyor': return <Package className="h-5 w-5 text-purple-500" />;
      case 'kargoda': return <Truck className="h-5 w-5 text-indigo-500" />;
      case 'teslim_edildi': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'iptal': return <XCircle className="h-5 w-5 text-red-500" />;
      // Eski değerler için backward compatibility
      case 'pending': return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'processing': return <Package className="h-5 w-5 text-blue-500" />;
      case 'shipped': return <Truck className="h-5 w-5 text-purple-500" />;
      case 'delivered': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'cancelled': return <XCircle className="h-5 w-5 text-red-500" />;
      default: return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'beklemede': return 'Beklemede';
      case 'onaylandi': return 'Onaylandı';
      case 'hazirlaniyor': return 'Hazırlanıyor';
      case 'kargoda': return 'Kargoda';
      case 'teslim_edildi': return 'Teslim Edildi';
      case 'iptal': return 'İptal Edildi';
      // Eski değerler için backward compatibility
      case 'pending': return 'Beklemede';
      case 'processing': return 'Hazırlanıyor';
      case 'shipped': return 'Kargoda';
      case 'delivered': return 'Teslim Edildi';
      case 'cancelled': return 'İptal Edildi';
      default: return status || 'Bilinmiyor';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'beklemede': return 'bg-yellow-100 text-yellow-800';
      case 'onaylandi': return 'bg-blue-100 text-blue-800';
      case 'hazirlaniyor': return 'bg-purple-100 text-purple-800';
      case 'kargoda': return 'bg-indigo-100 text-indigo-800';
      case 'teslim_edildi': return 'bg-green-100 text-green-800';
      case 'iptal': return 'bg-red-100 text-red-800';
      // Eski değerler için backward compatibility
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Tarih bilgisi yok';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Geçersiz tarih';
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Tarih hatası';
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || isNaN(price)) return '₺0,00';
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return '₺0,00';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numPrice);
  };

  if (authLoading || (loading && !loadError)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Siparişler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-700 mb-6">{loadError}</p>
          <button
            type="button"
            onClick={loadOrders}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Tekrar dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-8 overflow-x-hidden">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 w-full min-w-0">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Siparişlerim</h1>
              <p className="text-sm text-gray-600 mt-1">Tüm siparişlerinizi buradan takip edebilirsiniz</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 shrink-0">
              <Package className="h-5 w-5" />
              <span>{orders.length} sipariş</span>
            </div>
          </div>
        </div>

        {/* Orders List */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz siparişiniz yok</h3>
            <p className="text-gray-600 mb-6">İlk siparişinizi vermek için ürünlerimizi inceleyin</p>
            <a
              href="/products"
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Alışverişe Başla
            </a>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg shadow-sm overflow-hidden max-w-full">
                {/* Order Header */}
                <div className="p-4 sm:p-6 border-b border-gray-200">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between min-w-0">
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {getStatusIcon(order.order_status || order.status)}
                        <span className={`px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(order.order_status || order.status)}`}>
                          {getStatusText(order.order_status || order.status)}
                        </span>
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 flex items-center gap-1 min-w-0">
                        <Calendar className="h-4 w-4 shrink-0" />
                        <span className="truncate">{formatDate(order.created_at || order.createdAt || order.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-500 break-all">
                        Sipariş #{order.order_number || order.id || '-'}
                      </p>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 w-full sm:w-auto">
                      <p className="text-lg font-semibold text-gray-900 sm:text-right">
                        {formatPrice(order.total || order.total_amount || 0)}
                      </p>
                      <button
                        type="button"
                        onClick={() => setSelectedOrder(selectedOrder?.id === order.id ? null : order)}
                        className="flex items-center gap-1.5 px-3 py-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors text-sm"
                        aria-label="Sipariş detayları"
                      >
                        <Eye className="h-4 w-4 shrink-0" />
                        <span className="hidden sm:inline">Detaylar</span>
                      </button>
                    </div>
                  </div>
                </div>

                {order.order_status === 'kargoda' &&
                  (order.tracking_number || getOrderCarrierLabel(order)) && (
                  <div className="px-4 sm:px-6 py-4 bg-indigo-50 border-b border-indigo-100 flex flex-col gap-3 min-w-0">
                    <div className="flex items-start gap-3 min-w-0">
                      <Truck className="h-5 w-5 text-indigo-600 mt-0.5 shrink-0" />
                      <div className="text-sm min-w-0 flex-1">
                        <p className="font-semibold text-indigo-900">Kargo takibi</p>
                        <p className="text-gray-700 mt-0.5 break-words">
                          <span className="text-gray-500">Firma:</span>{' '}
                          {getOrderCarrierLabel(order) || '—'}
                        </p>
                        {order.tracking_number && (
                          <p className="text-gray-700 mt-0.5 break-all text-xs sm:text-sm">
                            <span className="text-gray-500 font-sans">Takip no:</span>{' '}
                            {order.tracking_number}
                          </p>
                        )}
                      </div>
                    </div>
                    {getOrderTrackingUrl(order) && (
                      <a
                        href={getOrderTrackingUrl(order)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
                      >
                        Kargoyu takip et
                        <ExternalLink className="h-4 w-4 shrink-0" />
                      </a>
                    )}
                  </div>
                )}

                {/* Order Items */}
                <div className="p-4 sm:p-6 min-w-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {order.items?.map((item, index) => (
                      <div
                        key={index}
                        className="flex flex-col gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100 min-w-0"
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          <img
                            src={getImageUrl(
                              typeof item.product?.images?.[0] === 'object'
                                ? item.product?.images?.[0]?.url
                                : item.product?.images?.[0] || item.image || '/images/placeholder.jpg'
                            )}
                            alt={item.product?.name || item.name}
                            className="w-14 h-14 object-contain rounded-lg border bg-white shrink-0"
                            crossOrigin="anonymous"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkdyw7xzw7xsIFlva8SxPC90ZXh0Pjwvc3ZnPg==';
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                              {item.product?.name || item.name || 'Ürün'}
                            </p>
                            <p className="text-sm text-gray-500 mt-0.5">Adet: {item.quantity || 1}</p>
                            <p className="text-sm font-semibold text-gray-900 mt-0.5">
                              {formatPrice((item.price || 0) * (item.quantity || 1))}
                            </p>
                          </div>
                        </div>
                        {order.order_status === 'teslim_edildi' && item.product_id && (
                          <Link
                            to={`/product/${item.product_id}#reviews`}
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3.5 text-base font-semibold text-white shadow-md hover:bg-orange-600 active:scale-[0.99] transition"
                          >
                            <Star className="h-5 w-5 fill-white" />
                            Ürünü değerlendir
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Details */}
                {selectedOrder?.id === order.id && (
                  <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Shipping Address */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Teslimat Adresi</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          <p className="font-semibold">{order.shipping_name || order.shippingAddress?.firstName + ' ' + order.shippingAddress?.lastName || '-'}</p>
                          <p>{order.shipping_address || order.shippingAddress?.address || '-'}</p>
                          <p>
                            {order.shipping_city || order.shippingAddress?.city || ''}
                            {(order.shipping_state || order.shippingAddress?.state) ? `, ${order.shipping_state || order.shippingAddress?.state}` : ''}
                          </p>
                          {(order.shipping_zip_code || order.shippingAddress?.zipCode) && (
                            <p>Posta Kodu: {order.shipping_zip_code || order.shippingAddress?.zipCode}</p>
                          )}
                          <p>Telefon: {order.shipping_phone || order.shippingAddress?.phone || order.contactInfo?.phone || '-'}</p>
                          {(order.guest_email || order.contactInfo?.email) && (
                            <p>E-posta: {order.guest_email || order.contactInfo?.email}</p>
                          )}
                        </div>
                      </div>

                      {/* Order Summary */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Sipariş Özeti</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Ürünler:</span>
                            <span className="font-medium">{formatPrice(order.subtotal || order.subtotal_amount || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Kargo:</span>
                            <span className="font-medium">
                              {(order.shipping_cost || order.shippingCost || 0) > 0 
                                ? formatPrice(order.shipping_cost || order.shippingCost || 0) 
                                : 'Ücretsiz'}
                            </span>
                          </div>
                          <div className="flex justify-between text-base font-semibold border-t border-gray-200 pt-2">
                            <span>Toplam:</span>
                            <span>{formatPrice(order.total || order.total_amount || 0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Order Notes */}
                    {order.notes && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Notlar</h4>
                        <p className="text-sm text-gray-600 bg-white p-3 rounded-lg border">
                          {order.notes}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;